import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    Injector,
    inject,
    input,
    type Signal,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageModule } from '@openng/optimus-ui/message';
import { PanelModule } from '@openng/optimus-ui/panel';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { DiffViewerComponent } from '@app/shared/components/diff-viewer/diff-viewer.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import {
    OBJECT_DISPLAY_NAMES,
    OBJECT_INFO,
    OBJECTS,
    SKELETON_TYPES,
} from '@app/shared/constants/shared.constant';
import { ObjectIconPipe } from '@app/shared/pipes/object-color.pipe';
import type { WithWorkflowableService } from '@app/shared/services/mixins/workflow.service';
import { Helpers } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';

export interface ObjectDiffSummary {
    object: OBJECTS;
    id: number;
    prevVersionId: number | null;
    newVersionId: number | null;
    name: string;
    action: 'added' | 'modified' | 'removed';
    additions: number;
    deletions: number;
}

export interface ObjectDiffData {
    old: {
        definition?: string | null;
        promptTemplate?: string | null;
        name?: string | null;
        alias?: string | null;
    };
    new: {
        definition?: string | null;
        promptTemplate?: string | null;
        name?: string | null;
        alias?: string | null;
    };
}

@Component({
    selector: 'cor-object-diff-viewer',
    standalone: true,
    imports: [
        CommonModule,
        PanelModule,
        ObjectIconPipe,
        DiffViewerComponent,
        TooltipModule,
        MessageModule,
        SkeletonComponent,
    ],
    templateUrl: './object-diff-viewer.component.html',
    styleUrl: './object-diff-viewer.component.scss',
})
export class ObjectDiffViewerComponent {
    readonly diffData = input.required<ObjectDiffSummary>();

    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly OBJECTS = OBJECTS;
    protected readonly OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;
    protected readonly OBJECT_INFO = OBJECT_INFO;
    protected readonly SKELETON_TYPES = SKELETON_TYPES;

    protected readonly isCollapsed = signal(true);
    protected readonly detail = signal<ObjectDiffData>(null);
    protected readonly isLoading = signal(false);
    protected readonly errorMessage = signal(null);
    protected readonly service: Signal<WithWorkflowableService> = computed(() =>
        this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.diffData().object]),
    );

    protected readonly diffSquares = computed(() => {
        const totalChanges = this.diffData().additions + this.diffData().deletions;
        const squares: ('addition' | 'deletion' | 'neutral')[] = [];

        const additionSquares = Math.floor((this.diffData().additions / totalChanges) * 5);
        const deletionSquares = Math.floor((this.diffData().deletions / totalChanges) * 5);

        for (let i = 0; i < additionSquares; i++) {
            squares.push('addition');
        }
        for (let i = 0; i < deletionSquares; i++) {
            squares.push('deletion');
        }
        while (squares.length < 5) {
            squares.push('neutral');
        }
        return squares;
    });

    protected onCollapsedChange(collapsed: boolean | undefined): void {
        this.isCollapsed.set(collapsed !== false);
        if (this.isCollapsed() || this.detail() || this.isLoading()) return;

        this.errorMessage.set(null);
        this.isLoading.set(true);
        this.service()
            .getLineageDiffItem(this.diffData().prevVersionId, this.diffData().newVersionId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ result }) => this.detail.set(result),
                error: error => this.errorMessage.set(Helpers.getErrorMessageString(error)),
            })
            .add(() => this.isLoading.set(false));
    }
}
